import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { IMG } from "@/lib/images";
import { INPUT } from "@/pages/admin/ui";
import { Lock } from "lucide-react";

export default function AdminLogin() {
  const nav = useNavigate();
  const { loginWithToken } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      loginWithToken(data.token, data.user);
      nav("/admin/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="relative hidden lg:block">
        <img src={IMG.chefKitchen} alt="DineOS" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-charcoal/70" />
        <div className="absolute inset-0 flex flex-col justify-between p-12 text-ivory">
          <div className="flex items-center gap-3">
            <img src={IMG.logo} alt="Mee2Momos" className="h-12 w-14 rounded-t-full border border-gold/40 bg-ivory object-contain p-1" />
            <div><div className="font-display text-2xl">Mee2Momos</div><div className="text-[10px] uppercase tracking-editorial text-gold">DineOS Operations</div></div>
          </div>
          <div>
            <h2 className="font-display text-4xl font-light">Operexa DineOS</h2>
            <p className="mt-3 max-w-sm text-ivory/70">The operational backbone for premium pure-veg & Jain-Friendly hospitality — events, food services, menu, staff and forecasting.</p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center bg-[#F8F9FA] p-6">
        <div className="w-full max-w-sm">
          <div className="mb-8 flex items-center gap-2 lg:hidden">
            <img src={IMG.logo} alt="Mee2Momos" className="h-10 w-11 rounded-t-full border border-gold/40 object-contain p-1" />
            <span className="font-display text-xl text-espresso">Mee2Momos DineOS</span>
          </div>
          <div className="mb-2 inline-flex items-center gap-2 rounded-full bg-[#2C201A]/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-[#2C201A]"><Lock className="h-3 w-3" /> Private Access</div>
          <h1 className="font-display text-3xl text-[#0F172A]">Sign in to DineOS</h1>
          <p className="mt-1 text-sm text-[#64748B]">Authorized operators only.</p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Email</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT} data-testid="login-email" /></div>
            <div><label className="text-[11px] font-semibold uppercase tracking-wider text-[#64748B]">Password</label>
              <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className={INPUT} data-testid="login-password" /></div>
            {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
            <button type="submit" disabled={loading} data-testid="login-submit-button" className="w-full rounded-md bg-[#2C201A] py-3 text-sm font-semibold uppercase tracking-wider text-white hover:bg-[#C5A059] disabled:opacity-60">
              {loading ? "Signing in…" : "Sign In"}
            </button>
          </form>

          <div className="my-6 flex items-center gap-3 text-xs text-[#94A3B8]"><span className="h-px flex-1 bg-[#E2E8F0]" /> OR <span className="h-px flex-1 bg-[#E2E8F0]" /></div>
          <button onClick={googleLogin} data-testid="login-google-button" className="flex w-full items-center justify-center gap-2 rounded-md border border-[#E2E8F0] bg-white py-3 text-sm font-medium text-[#334155] hover:bg-[#F8F9FA]">
            <img src="https://www.google.com/favicon.ico" alt="" className="h-4 w-4" /> Continue with Google
          </button>
        </div>
      </div>
    </div>
  );
}

export function AuthCallback() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/admin"); return; }
    const sid = decodeURIComponent(m[1]);
    fetch(`${API}/auth/google/session`, { method: "POST", headers: { "X-Session-ID": sid } })
      .then((r) => r.ok ? r.json() : Promise.reject())
      .then((data) => {
        localStorage.setItem("m2m_token", data.session_token);
        setUser(data.user);
        window.history.replaceState(null, "", "/admin/dashboard");
        nav("/admin/dashboard");
      })
      .catch(() => nav("/admin"));
  }, []); // eslint-disable-line
  return <div className="grid min-h-screen place-items-center bg-[#F8F9FA] text-[#64748B]">Signing you in…</div>;
}
